import PropTypes from "prop-types"
import { forwardRef } from "react"

import Button from "components/ui/button"
import Image from "components/ui/image"
import { cn } from "styles/utils"

export const GradientContainer = ({
  className = "",
  shouldUseNewWizardExp,
  hideSlideShadow = false,
  ...props
}) => (
  <div
    className={cn(
      "tablet:after:hidden relative overflow-hidden",
      !hideSlideShadow &&
        "after:bg-linear-to-r after:w-1/10 after:pointer-events-none after:absolute after:right-0 after:top-0 after:block after:h-full after:from-white/0 after:from-0% after:to-white after:to-95%",
      !shouldUseNewWizardExp && "tablet:px-0 px-12",
      className
    )}
    {...props}
  />
)
GradientContainer.propTypes = {
  className: PropTypes.string,
  hideSlideShadow: PropTypes.bool,
  shouldUseNewWizardExp: PropTypes.bool,
}

export const OptionsLabel = ({ className = "", ...props }) => (
  <p className={cn("text-14 font-bold text-black", className)} {...props} />
)

OptionsLabel.propTypes = {
  className: PropTypes.string,
}

export const OptionsSlider = forwardRef(
  (
    { className = "", hideSlideShadow, modal, shouldUseNewWizardExp, ...props },
    ref
  ) => (
    <div
      className={cn(
        "tablet:flex-wrap tablet:overflow-x-auto tablet:last:after:hidden flex items-stretch overflow-y-hidden overflow-x-scroll",
        modal ? "justify-between" : "justify-start",
        shouldUseNewWizardExp && "desktop:gap-x-20 gap-x-16 gap-y-16 pb-16",
        !hideSlideShadow && "tablet:pr-0 pr-[10%]",
        className
      )}
      ref={ref}
      {...props}
    />
  )
)

OptionsSlider.displayName = "OptionsSlider"

OptionsSlider.propTypes = {
  className: PropTypes.string,
  hideSlideShadow: PropTypes.bool,
  modal: PropTypes.bool,
  shouldUseNewWizardExp: PropTypes.bool,
}

export const ChoiceOption = forwardRef(
  ({ className = "", shouldUseNewWizardExp, ...props }, ref) => (
    <Button
      className={cn(
        shouldUseNewWizardExp
          ? "tablet:basis-[73px] aria-checked:border-primary focus:outline-primary desktop:p-8 relative mx-4 mb-10 flex shrink-0 grow-0 basis-[21%] cursor-pointer flex-col items-center justify-stretch gap-4 border-2 border-transparent p-6 text-center -outline-offset-2 focus:outline-2"
          : "wizard-choice-option",
        className
      )}
      ref={ref}
      {...props}
    />
  )
)

ChoiceOption.displayName = "ChoiceOption"

ChoiceOption.propTypes = {
  className: PropTypes.string,
  shouldUseNewWizardExp: PropTypes.bool,
}

export const ChoiceLabel = forwardRef(
  ({ className, withSelectState = false, ...props }, ref) => (
    <label
      className={cn(
        "group relative flex cursor-pointer flex-col items-center justify-start gap-6 text-center",
        withSelectState &&
          "has-checked:border-primary has-focus:outline-primary has-checked:bg-primary/10 has-focus:outline-2 border-2 border-transparent -outline-offset-2",
        className
      )}
      ref={ref}
      {...props}
    />
  )
)

ChoiceLabel.displayName = "ChoiceLabel"

ChoiceLabel.propTypes = {
  className: PropTypes.string,
  withSelectState: PropTypes.bool,
}

export const ChoiceSwatch = ({
  className,
  disableSelectState = false,
  ...props
}) => (
  <div
    aria-hidden
    className={cn(
      "text-14 border-grey-300 size-46 flex items-center justify-center overflow-hidden rounded-full border font-semibold tracking-tight -outline-offset-2",
      !disableSelectState &&
        "peer-checked:border-primary peer-checked:bg-primary/10 peer-focus:outline-primary peer-checked:border-2 peer-focus:outline-2",
      className
    )}
    {...props}
  />
)

ChoiceSwatch.propTypes = {
  className: PropTypes.string,
  disableSelectState: PropTypes.bool,
}

export const OptionTitle = ({
  className = "",
  shouldUseNewWizardExp,
  ...props
}) => (
  <p
    className={cn(
      "text-grey-70 text-12 mx-2 my-8",
      shouldUseNewWizardExp && "text-black-print my-0 font-medium",
      className
    )}
    {...props}
  />
)

OptionTitle.propTypes = {
  className: PropTypes.string,
  shouldUseNewWizardExp: PropTypes.bool,
}

export const OptionPrice = ({
  className = "",
  shouldUseNewWizardExp,
  ...props
}) => (
  <p
    className={cn(
      shouldUseNewWizardExp
        ? "text-12 text-black-print font-semibold tracking-tight"
        : "text-11 tablet:text-12 tracking-half font-bold text-black",
      className
    )}
    {...(shouldUseNewWizardExp && { "aria-hidden": true })}
    {...props}
  />
)

OptionPrice.propTypes = {
  className: PropTypes.string,
  shouldUseNewWizardExp: PropTypes.bool,
}

export const OptionsSelect = ({ className, ...props }) => (
  <select className={cn("w-3/4 bg-white px-10 py-8", className)} {...props} />
)

OptionsSelect.propTypes = {
  className: PropTypes.string,
}

export const OptionImage = ({ className, ...props }) => (
  <Image
    className={cn("aspect-square", className)}
    height="40"
    width="40"
    {...props}
  />
)

OptionImage.propTypes = {
  className: PropTypes.string,
}

export const SkuIcon = ({
  className = "",
  shouldUseNewWizardExp,
  skuColor = "W",
  ...props
}) => (
  <div
    className={cn(
      "bg-grey-20 flex items-center justify-center rounded-full",
      skuColor === "Y" && "bg-sku-ky",
      skuColor === "R" && "bg-sku-kr",
      skuColor === "CE" && "bg-sku-cer text-white",
      skuColor === "W" && "bg-sku-kw",
      skuColor === "V" && "bg-grey-20 border-sku-ky border-8",
      skuColor === "YGP" && "bg-grey-20 border-6 border-sku-ky",
      shouldUseNewWizardExp
        ? "size-46"
        : "tablet:mx-24 tablet:mb-8 mx-16 mb-8 mt-0 size-48",
      className
    )}
    {...props}
  />
)

SkuIcon.propTypes = {
  className: PropTypes.string,
  shouldUseNewWizardExp: PropTypes.bool,
  skuColor: PropTypes.string,
}

export const ErrorBox = (props) => (
  <div
    className="text-sale text-14 mb-16 flex items-center rounded-sm bg-[#e3352a26] p-8"
    {...props}
  />
)
export const StoneTable = ({ className, ...props }) => (
  <table
    className={cn(
      "desktop:w-11/20 max-w-250 mx-auto mb-24 w-3/4 text-left",
      className
    )}
    {...props}
  />
)
StoneTable.propTypes = {
  className: PropTypes.string,
}

export const Tag = ({ className, primary, ...props }) => (
  <div
    className={cn(
      "before:-bottom-15 after:-bottom-15 text-11 leading-21 min-w-21 before:border-5 after:border-5 relative bottom-8 inline-block px-8 py-0 text-white before:absolute before:left-1/2 before:block before:h-0 before:w-0 before:-translate-x-1/2 before:-translate-y-1/2 before:border-transparent after:absolute after:left-1/2 after:block after:h-0 after:w-0 after:-translate-x-1/2 after:-translate-y-1/2 after:border-transparent empty:bg-transparent",
      primary
        ? "bg-primary after:border-t-primary before:border-t-primary"
        : "bg-black before:border-t-black after:border-t-black",
      className
    )}
    {...props}
  />
)
Tag.propTypes = {
  className: PropTypes.string,
  primary: PropTypes.bool,
}
